import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authService } from '../../services/httpServices/authService';

export interface User {
  id: string;
  username: string;
  name: string;
  roles: string[];
  customRoles?: {
    id: string;
    name: string;
    isWebsiteOnly?: boolean;
    permissions: { id: string; code: string }[];
  }[];
  department?: string;
  /** True while a super admin is viewing the app as another user. */
  impersonating?: boolean;
  /** Display name of the original super admin account while impersonating. */
  impersonatorName?: string;
}

export interface AuthState {
  user: User | null;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;
  isAuthenticated: boolean;
}

const initialState: AuthState = {
  user: null,
  isLoading: true,
  isInitialized: false,
  error: null,
  isAuthenticated: false,
};

function mapToUser(data: any): User | null {
  if (!data || typeof data !== 'object') return null;
  const firstName = data.firstName || '';
  const lastName = data.lastName || '';
  const name = data.name || `${firstName} ${lastName}`.trim() || data.username || 'User';
  const impersonatorName = data.impersonator
    ? `${data.impersonator.firstName || ''} ${data.impersonator.lastName || ''}`.trim() ||
      data.impersonator.username ||
      undefined
    : undefined;

  return {
    id: data.id,
    username: data.username,
    name,
    roles: data.roles || [],
    customRoles: data.customRoles || [],
    department: data.department,
    impersonating: data.impersonating ?? false,
    impersonatorName,
  };
}

export const login = createAsyncThunk(
  'auth/login',
  async (
    { username, password, rememberMe }: { username: string; password: string; rememberMe: boolean },
    { rejectWithValue },
  ) => {
    try {
      const response = await authService.login({ username, password, rememberMe });
      const user = mapToUser(response.data);
      if (!user) return rejectWithValue('Invalid user data received from server');
      return { user };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Login failed');
    }
  },
);

export const logout = createAsyncThunk('auth/logout', async () => {
  try {
    await authService.logout();
  } catch {
    // Ignore logout errors — backend clears cookies regardless
  }
});

/**
 * Super admin only: start viewing the app as another user's account. The
 * backend swaps the session cookies; callers reload the page afterwards so all
 * cached queries/state are rebuilt for the new identity.
 */
export const switchAccount = createAsyncThunk(
  'auth/switchAccount',
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await authService.impersonate(userId);
      const user = mapToUser(response.data);
      if (!user) return rejectWithValue('Invalid user data received from server');
      return { user: { ...user, impersonating: true } };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to switch account');
    }
  },
);

/** Return to the original super admin account after switching. */
export const switchBack = createAsyncThunk(
  'auth/switchBack',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.stopImpersonation();
      const user = mapToUser(response.data);
      if (!user) return rejectWithValue('Invalid user data received from server');
      return { user: { ...user, impersonating: false, impersonatorName: undefined } };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to switch back');
    }
  },
);

export const initializeAuth = createAsyncThunk(
  'auth/initialize',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.me();
      const user = mapToUser(response.data);
      if (!user) return rejectWithValue('Invalid user data received from server');
      return { user };
    } catch {
      // The HTTP interceptor already attempts a single token refresh on 401 and
      // redirects to /login when it fails. Refreshing again here would retry a
      // dead session indefinitely on the login page.
      return rejectWithValue('Session expired');
    }
  },
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setInitialized: (state) => {
      state.isInitialized = true;
      state.isLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isInitialized = true;
        state.user = action.payload.user;
        state.isAuthenticated = true;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.isInitialized = true;
        state.error = action.payload as string;
        state.isAuthenticated = false;
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.error = null;
      })
      .addCase(logout.rejected, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.error = null;
      })
      .addCase(switchAccount.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.isAuthenticated = true;
      })
      .addCase(switchBack.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.isAuthenticated = true;
      })
      .addCase(initializeAuth.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isInitialized = true;
        if (action.payload) {
          state.user = action.payload.user;
          state.isAuthenticated = true;
        } else {
          state.isAuthenticated = false;
        }
      })
      .addCase(initializeAuth.rejected, (state) => {
        state.isLoading = false;
        state.isInitialized = true;
        state.isAuthenticated = false;
        state.user = null;
      });
  },
});

export const { clearError, setInitialized } = authSlice.actions;
export default authSlice.reducer;
